// BRE's fiscal calendar, by date (ADR 0050). One org-wide calendar: board
// governance, grants and any other report grouped by fiscal year read it,
// so no module hard-codes a July start. Names keep their governance prefix
// for compatibility; breFiscalYear is the org-wide alias.

// The fiscal year a date falls in for a fixed start month: with a July
// start, 2026-08-01 is FY2027. Pure month arithmetic; for BRE's real years
// use breFiscalYear (the calendar below), not this.
export function fiscalYearOf(day: string, startMonth: number): number {
  const [y, m] = day.split('-').map(Number) as [number, number]
  return startMonth === 1 ? y : m >= startMonth ? y + 1 : y
}

// The calendar (ADR 0050; owner, 2026-10-24). A small
// explicit table rather than a setting read at run time: every screen and
// the import need the fiscal year of a date synchronously, and the past
// never changes.
//
// - Through December 31, 2025 the fiscal year was the calendar year
//   (every bylaws version before November 24, 2025): FY2018 is calendar
//   2018, ..., FY2025 is calendar 2025.
// - January 1 to June 30, 2026 is the short transition year, FY2026
//   ("FY2026 (short year, Jan–Jun 2026)"). Its number, 2026, can't collide:
//   calendar FY2025 ends before it and FY2027 starts after it.
// - From July 1, 2026 the year runs July 1 to June 30 and is named for the
//   year it ends: FY2027 is July 1, 2026 through June 30, 2027.
//
// The `fiscalYear.startMonth` setting must agree with the calendar's
// current segment: a settings version with another start month is refused
// (fiscalCalendarProblems), because moving the fiscal year again needs a
// dated change to this table, not just a new number.
export type FiscalCalendarSegment = { from: string | null; to: string | null; kind: 'calendar' | 'short' | 'july'; startMonth: number; label: string }
export const GOVERNANCE_FISCAL_CALENDAR: readonly FiscalCalendarSegment[] = [
  { from: null, to: '2025-12-31', kind: 'calendar', startMonth: 1, label: 'Calendar year (January 1 to December 31), through 2025' },
  { from: '2026-01-01', to: '2026-06-30', kind: 'short', startMonth: 1, label: 'Short transition year, January 1 to June 30, 2026' },
  { from: '2026-07-01', to: null, kind: 'july', startMonth: 7, label: 'July 1 to June 30, from July 1, 2026' },
]
export const LAST_CALENDAR_FISCAL_YEAR = 2025
export const SHORT_FISCAL_YEAR = 2026
// The fiscal year whose first day is the first July 1 start (2026-07-01).
export const FIRST_JULY_FISCAL_YEAR = 2027
// The start month of the fiscal year in force from now on.
export const GOVERNANCE_FISCAL_YEAR_START_MONTH = 7

// The fiscal year a date falls in: 2025-12-31 is FY2025, 2026-01-01 and
// 2026-06-30 are FY2026 (the short year), 2026-07-01 is FY2027.
export function governanceFiscalYear(day: string): number {
  if (day <= '2025-12-31') return Number(day.slice(0, 4))
  if (day <= '2026-06-30') return SHORT_FISCAL_YEAR
  return fiscalYearOf(day, GOVERNANCE_FISCAL_YEAR_START_MONTH)
}

// The first and last day of a fiscal year.
export function fiscalYearRange(fy: number): { from: string; to: string } {
  if (fy <= LAST_CALENDAR_FISCAL_YEAR) return { from: `${fy}-01-01`, to: `${fy}-12-31` }
  if (fy === SHORT_FISCAL_YEAR) return { from: '2026-01-01', to: '2026-06-30' }
  return { from: `${fy - 1}-07-01`, to: `${fy}-06-30` }
}

export const isShortFiscalYear = (fy: number) => fy === SHORT_FISCAL_YEAR

// "FY2025", "FY2026 (short year, Jan–Jun 2026)", "FY2027".
export function fiscalYearLabel(fy: number): string {
  return fy === SHORT_FISCAL_YEAR ? 'FY2026 (short year, Jan–Jun 2026)' : `FY${fy}`
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
// "January 1 to December 31, 2025", "January 1 to June 30, 2026",
// "July 1, 2026 to June 30, 2027".
export function fiscalYearSpanLabel(fy: number): string {
  const { from, to } = fiscalYearRange(fy)
  const day = (d: string) => `${MONTH_NAMES[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`
  return from.slice(0, 4) === to.slice(0, 4) ? `${day(from)} to ${day(to)}, ${to.slice(0, 4)}` : `${day(from)}, ${from.slice(0, 4)} to ${day(to)}, ${to.slice(0, 4)}`
}

// "FY2027 (July 1, 2026 to June 30, 2027)"; the short year's label already
// says its months.
export const fiscalYearLongLabel = (fy: number) => (fy === SHORT_FISCAL_YEAR ? fiscalYearLabel(fy) : `${fiscalYearLabel(fy)} (${fiscalYearSpanLabel(fy)})`)

// A yearly due date (MM-DD) inside a fiscal year: the one day with that
// month and day in the year's range, or the year's last day when the short
// year has none (a September due date in January–June 2026).
export function dueOnInFiscalYear(fy: number, monthDay: string): string {
  const { from, to } = fiscalYearRange(fy)
  for (const y of new Set([from.slice(0, 4), to.slice(0, 4)])) {
    const d = `${y}-${monthDay}`
    if (d >= from && d <= to) return d
  }
  return to
}

// Whether the elected terms of a fiscal year begin on its first day. Under
// the bylaws before November 24, 2025 they began January 1; the short year
// began none (terms that would have ended December 31, 2025 were extended
// to June 30, 2026); from July 1, 2026 they begin every July 1. Hub's
// rollover notice runs only for July 1 starts (FY2027 on).
export const termsBeginWithFiscalYear = (fy: number) => fy !== SHORT_FISCAL_YEAR
export const rolloverNoticeFor = (fy: number) => fy >= FIRST_JULY_FISCAL_YEAR

// The org-wide name for governanceFiscalYear: grants, reports and
// anything else grouped by fiscal year use this.
export const breFiscalYear = governanceFiscalYear
